import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, FileX, Scale, MapPin, Sparkles, ChevronRight, 
  Mail, Loader2, Eraser, Eye, Building2, 
  User, Printer, ShieldCheck, Zap, Layers, FileSignature
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';

// UI Components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster'; 

// Context & Services
import { useLanguage } from '../context/LanguageContext'; // 🔥 Dil Entegrasyonu
import { useAuth } from '../context/AuthContext';
import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';
import { apiService } from '../services/api';

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // 🔥 Dil Fonksiyonunu Al
  const { language, t } = useLanguage(); 
  
  const { user } = useAuth();
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  
  const fileInputRef = useRef(null);
  const sigCanvas = useRef({});

  const [formData, setFormData] = useState({
    senderName: user?.name || '',
    senderAddress: user?.companyDetails?.street ? `${user.companyDetails.street}, ${user.companyDetails.zipCity}` : '',
    recipientName: '',
    recipientAddress: '',
    customerNumber: '',
    date: new Date().toLocaleDateString(language === 'tr' ? 'tr-TR' : 'de-DE') 
  });

  const templates = [
    { id: 'cancellation', title: t('tpl_termination'), icon: FileX, color: 'text-rose-400', bg: 'bg-rose-500/10', desc: t('tpl_termination_desc') },
    { id: 'objection', title: t('tpl_objection'), icon: Scale, color: 'text-amber-400', bg: 'bg-amber-500/10', desc: t('tpl_objection_desc') },
    { id: 'address_change', title: t('tpl_relocation'), icon: MapPin, color: 'text-blue-400', bg: 'bg-blue-500/10', desc: t('tpl_relocation_desc') }
  ];

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🦊 FOX-AI: Belge Analizi ve Otomatik Form Doldurma
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setAnalyzing(true);
    toast({ title: "Fox-AI Scanning", description: "Extracting metadata from document..." });

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
        const response = await apiService.templates.analyze(uploadData);
        const data = response.data;

        setFormData(prev => ({ 
            ...prev, 
            recipientName: data.recipientName || '', 
            customerNumber: data.customerNumber || '',
            recipientAddress: data.recipientAddress || ''
        }));
        
        toast({ title: "Success", description: "Data extracted and form populated." });
    } catch (error) {
        toast({ title: "Analysis Error", description: "Could not read the document.", variant: "destructive" });
    } finally {
        setAnalyzing(false);
    }
  };

  // 📄 PDF ÖNİZLEME OLUŞTURMA
  const handlePreview = async () => {
    if (!formData.recipientName) {
        toast({ title: "Error", description: "Please enter recipient name.", variant: "destructive" });
        return;
    }

    setLoading(true);
    
    // İmza verisini alırken hata oluşmaması için kontrol
    let signatureData = null;
    try {
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        const canvas = sigCanvas.current.getTrimmedCanvas ? sigCanvas.current.getTrimmedCanvas() : sigCanvas.current.getCanvas();
        signatureData = canvas.toDataURL('image/png');
      }
    } catch (e) {
      console.warn("Signature extraction issue, using basic canvas.");
      signatureData = sigCanvas.current.getCanvas().toDataURL('image/png');
    }

    const payload = {
        ...formData,
        type: selectedTemplate.id,
        signature: signatureData,
        language: language
    };

    try {
        const response = await apiService.templates.generate(payload);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
        
        const url = URL.createObjectURL(blob);
        setPdfPreviewUrl(url);
        toast({ title: "Document Ready", description: "Preview updated in Live Terminal." });
    } catch (error) {
        toast({ title: "Error", description: "Failed to generate document preview.", variant: "destructive" });
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-sans pb-32 flex flex-col relative overflow-x-hidden">
      <Toaster />
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf" onChange={handleFileChange} />

      {/* HEADER */}
      <header className="relative z-50 sticky top-0 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-4">
            {selectedTemplate && (
                <button onClick={() => { setSelectedTemplate(null); setPdfPreviewUrl(null); }} className="p-2 bg-white/5 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-all transform active:scale-90 border border-white/10">
                    <ArrowLeft size={18} />
                </button>
            )}
            <div>
                <h1 className="text-xl md:text-2xl font-black tracking-tighter uppercase italic text-white leading-none">
                    {selectedTemplate ? selectedTemplate.title : (t('docu_title') || 'Document Generator')}
                </h1>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mt-1">Fox-AI Legal Assistant v4.0</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
             <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400">
                <ShieldCheck size={14} />
                <span className="text-[9px] font-black uppercase tracking-widest">Verified DIN-5008</span>
             </div>
             <HeaderActions />
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 md:p-10 max-w-[1600px] mx-auto w-full relative z-10">
        {!selectedTemplate ? (
            /* --- SEÇİM EKRANI --- */
            <div className="space-y-12">
                <div className="space-y-2 max-w-2xl">
                    <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter uppercase italic leading-tight">
                        {t('choose_process') || 'Select Your'} <br/><span className="text-blue-500">Legal Terminal</span>
                    </h2>
                    <p className="text-slate-400 font-medium text-base">{t('choose_process_desc') || 'Legally compliant. DIN-5008 Standard.'}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {templates.map((template) => (
                        <motion.button 
                            whileHover={{ y: -8, scale: 1.02 }}
                            key={template.id} 
                            onClick={() => setSelectedTemplate(template)} 
                            className="flex flex-col p-10 glass-panel rounded-[3rem] shadow-2xl group text-left relative overflow-hidden transition-all h-[380px] border-white/5"
                        >
                            <div className={`p-5 rounded-3xl ${template.bg} w-fit mb-8 group-hover:rotate-12 transition-transform shadow-inner`}>
                                <template.icon size={36} className={template.color} />
                            </div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-4 italic">{template.title}</h3>
                            <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8 flex-1">{template.desc}</p>
                            <div className="mt-auto flex items-center text-[11px] font-black uppercase tracking-[0.3em] text-blue-400 group-hover:gap-3 transition-all">
                                {t('start_protocol') || 'Start Protocol'} <ChevronRight size={16} />
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>
        ) : (
            /* --- EDİTÖR --- */
            <div className="flex flex-col lg:flex-row gap-12 items-start">
                
                {/* SOL: FORM */}
                <div className="flex-1 space-y-8 w-full">
                    
                    {/* AI AUTO-FILL BANNER */}
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => fileInputRef.current.click()} 
                        className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-900 rounded-[2.5rem] p-10 text-white cursor-pointer group shadow-2xl border border-white/10 active:scale-[0.98] transition-all"
                    >
                        <div className="relative z-10 flex items-center gap-6">
                            <div className="p-4 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 group-hover:scale-110 transition-transform shadow-2xl">
                                {analyzing ? <Loader2 className="animate-spin" size={28}/> : <Sparkles size={28} className="text-orange-300 animate-pulse" />}
                            </div>
                            <div>
                                <h3 className="text-xl font-black uppercase tracking-tighter italic">Fox-AI Auto-Fill</h3>
                                <p className="text-xs text-blue-100 font-medium mt-1 opacity-80">Upload contract PDF, let Fox fill the fields.</p>
                            </div>
                        </div>
                        <Zap size={140} className="absolute -right-8 -bottom-8 opacity-10 rotate-12 group-hover:scale-110 transition-transform" />
                    </motion.div>

                    {/* FORM BOX */}
                    <div className="glass-panel rounded-[3rem] p-10 space-y-12 border-white/5">
                        <div className="space-y-8">
                            <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.4em] flex items-center gap-3"><User size={16}/> SENDER (ABSENDER)</h4>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('full_name') || 'Full Name'}</Label>
                                    <Input name="senderName" value={formData.senderName} onChange={handleInputChange} className="h-14 rounded-2xl bg-black/20 border-white/10 text-white font-bold focus:ring-2 ring-blue-500/20"/>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('address') || 'Address'}</Label>
                                    <Input name="senderAddress" value={formData.senderAddress} onChange={handleInputChange} className="h-14 rounded-2xl bg-black/20 border-white/10 font-medium text-white"/>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-8 pt-8 border-t border-white/5">
                            <h4 className="text-[10px] font-black text-orange-500 uppercase tracking-[0.4em] flex items-center gap-3"><Building2 size={16}/> RECIPIENT (EMPFÄNGER)</h4>
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('recipient_label') || 'Company Name'}</Label>
                                    <Input name="recipientName" value={formData.recipientName} onChange={handleInputChange} placeholder={t('recipient_placeholder') || "e.g. Vodafone GmbH"} className="h-16 rounded-2xl bg-black/20 border-white/10 font-black text-lg text-white"/>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('address') || 'Full Address'}</Label>
                                    <Input name="recipientAddress" value={formData.recipientAddress} onChange={handleInputChange} placeholder="Street, ZIP City" className="h-14 rounded-2xl bg-black/20 border-white/10 text-white"/>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-8 pt-8 border-t border-white/5">
                            <h4 className="text-[10px] font-black text-purple-500 uppercase tracking-[0.4em] flex items-center gap-3"><Layers size={16}/> REFERENCES</h4>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('customer_no') || 'Customer No.'}</Label>
                                    <Input name="customerNumber" value={formData.customerNumber} onChange={handleInputChange} className="h-14 rounded-2xl bg-black/20 border-white/10 font-mono text-blue-400 font-black"/>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('date') || 'Date'}</Label>
                                    <div className="h-14 rounded-2xl bg-black/20 flex items-center px-4 text-slate-400 font-bold italic border border-white/10">{formData.date}</div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 pt-8 border-t border-white/5">
                            <div className="flex justify-between items-center">
                                <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.4em] flex items-center gap-3"><FileSignature size={16}/> {t('signature') || 'Signature'}</h4>
                                <button onClick={() => sigCanvas.current.clear()} className="text-[10px] font-black text-rose-500 uppercase flex items-center gap-2 hover:bg-rose-500/10 px-3 py-1.5 rounded-lg transition-all">
                                    <Eraser size={14}/> {t('clear') || 'Clear'}
                                </button>
                            </div>
                            <div className="border-2 border-dashed border-white/10 rounded-[2.5rem] bg-black/30 h-48 relative cursor-crosshair group hover:border-blue-500/30 transition-all">
                                <SignatureCanvas ref={sigCanvas} penColor="#3b82f6" canvasProps={{ className: 'w-full h-full' }} />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
                                    <p className="text-xs font-black uppercase tracking-[1em] text-white">SIGN HERE</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SAĞ: ÖNİZLEME */}
                <div className="lg:w-[450px] w-full lg:sticky lg:top-28">
                    <div className="glass-panel rounded-[3rem] shadow-[0_40px_80px_rgba(0,0,0,0.6)] p-8 border-blue-500/10">
                        <div className="flex justify-between items-center mb-8">
                            <h3 className="font-black text-white uppercase italic tracking-tighter flex items-center gap-3"><Eye size={20} className="text-orange-500 animate-pulse"/> LIVE PREVIEW</h3>
                            {pdfPreviewUrl && <Badge className="bg-emerald-500 text-white border-0 text-[9px] px-3 py-1 animate-bounce">READY</Badge>}
                        </div>

                        <div className="w-full aspect-[1/1.41] bg-[#020617] rounded-[2rem] border border-white/5 overflow-hidden relative shadow-inner">
                            {pdfPreviewUrl ? (
                                <iframe src={`${pdfPreviewUrl}#toolbar=0&navpanes=0`} className="w-full h-full" title="PDF Preview" />
                            ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center text-center p-12 space-y-6">
                                    <div className="w-24 h-24 bg-white/5 rounded-[2rem] flex items-center justify-center text-slate-600 border border-white/5 shadow-2xl">
                                        <Printer size={40} />
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-sm text-slate-300 font-black uppercase tracking-widest italic">{t('waiting_input') || 'Waiting for Input'}</p>
                                        <p className="text-[10px] text-slate-500 font-medium leading-relaxed">Fill the form to see your legal document.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 mt-10">
                            <Button onClick={handlePreview} disabled={loading} variant="outline" className="h-16 rounded-2xl border-white/10 bg-white/5 text-white font-black uppercase tracking-[0.2em] text-xs hover:bg-white/10 transition-all active:scale-95">
                                {loading ? <Loader2 className="animate-spin" /> : 'Update Preview'}
                            </Button>
                            <Button onClick={handlePreview} disabled={loading} className="h-20 rounded-2xl bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 text-white font-black uppercase tracking-[0.3em] text-xs shadow-2xl active:scale-[0.95] transition-all flex items-center justify-center gap-4">
                                <Mail size={24} /> {t('send_mail') || 'Send & Download'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TemplatesPage;